import { useRef, useCallback, useEffect, useState } from 'react';

/**
 * Hook for intelligent auto-scrolling during AI chat streaming
 *
 * Manages auto-scroll state based on user scroll position:
 * - Enables auto-scroll when streaming starts if user is near bottom
 * - Disables auto-scroll when user scrolls up
 * - Re-enables auto-scroll when user scrolls back to near bottom
 *
 * Uses a ref+state dual pattern for isAutoScrollEnabled:
 * - Ref for synchronous reads in callbacks (no stale closure)
 * - State for React subscriptions (debugging, future UI use)
 * Same philosophy as the sync isProgrammaticScroll flag in 3871311.
 *
 * @param {Object} options
 * @param {React.RefObject} options.containerRef - Ref to scrollable container
 * @param {boolean} options.isStreaming - Whether content is currently streaming
 * @param {number} options.initialThreshold - Threshold to enable auto-scroll at start (default 0.05 = 5%)
 * @param {number} options.reEnableThreshold - Threshold to re-enable during scroll (default 0.03 = 3%)
 * @returns {{ onContentUpdate: Function, scrollToBottom: Function }}
 */
export function useStreamingAutoScroll({
  containerRef,
  isStreaming = false,
  initialThreshold = 0.05,
  reEnableThreshold = 0.02,
}) {
  // Auto-scroll enabled: ref for synchronous access, state for React
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(false);
  const isAutoScrollEnabledRef = useRef(false);

  // Track previous scroll position for direction detection
  const previousScrollTop = useRef(0);

  // Position signature to identify programmatic scrolls
  const programmaticScrollTarget = useRef(null);

  // RAF handles for cleanup
  const scrollRafHandle = useRef(null);
  const listenerRafHandle = useRef(null);

  /**
   * Update auto-scroll enabled state (both ref and state atomically)
   */
  const setAutoScroll = useCallback((enabled) => {
    isAutoScrollEnabledRef.current = enabled;
    setIsAutoScrollEnabled(enabled);
  }, []);

  /**
   * Check if container is near bottom
   * @param {HTMLElement} container - Scrollable container
   * @param {number} thresholdPercent - Threshold as percentage (0.05 = 5%)
   * @returns {boolean}
   */
  const isNearBottom = useCallback((container, thresholdPercent) => {
    if (!container) return false;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;

    // Use minimum 10px threshold for short content
    const thresholdPx = Math.max(scrollHeight * thresholdPercent, 10);

    return scrollBottom <= thresholdPx;
  }, []);


  /**
   * Scroll to bottom of container
   */
  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // Cancel any pending scroll RAF
    if (scrollRafHandle.current) {
      cancelAnimationFrame(scrollRafHandle.current);
    }

    scrollRafHandle.current = requestAnimationFrame(() => {
      // Calculate and record target position BEFORE scrolling
      const targetPosition = container.scrollHeight - container.clientHeight;
      programmaticScrollTarget.current = targetPosition;

      // Scroll to target
      container.scrollTop = targetPosition;

      // No timeout needed! Position signature remains valid until overwritten
    });
  }, [containerRef]);

  /**
   * Content update callback for StreamingMessage
   * Called when streaming content updates.
   * Reads ref directly — takes effect immediately when user scrolls up,
   * no waiting for React render propagation through component tree.
   */
  const onContentUpdate = useCallback(() => {
    if (!isAutoScrollEnabledRef.current) return;
    scrollToBottom();
  }, [scrollToBottom]);

  /**
   * Initialize auto-scroll state when streaming starts
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isStreaming) return;

    // Initialize previous scroll position
    previousScrollTop.current = container.scrollTop;

    // Check if user is near bottom when streaming starts
    const shouldEnableAutoScroll = isNearBottom(container, initialThreshold);
    setAutoScroll(shouldEnableAutoScroll);
  }, [isStreaming, containerRef, initialThreshold, isNearBottom, setAutoScroll]);

  /**
   * Handle user scroll events
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let throttled = false;

    const handleScroll = () => {
      // Check if current position matches programmatic target
      const currentPosition = container.scrollTop;
      const target = programmaticScrollTarget.current;

      // If position matches target (within 1px for floating point), it's programmatic
      if (target !== null && Math.abs(currentPosition - target) < 1) {
        return; // Ignore programmatic scroll
      }

      // User scroll detected — immediately disable auto-scroll via ref
      // This prevents any in-flight onContentUpdate from calling scrollToBottom
      // before React propagates the state change through the component tree
      isAutoScrollEnabledRef.current = false;

      // Cancel any pending scrollToBottom RAF
      if (scrollRafHandle.current) {
        cancelAnimationFrame(scrollRafHandle.current);
        scrollRafHandle.current = null;
      }

      // Clear programmatic target so future events aren't falsely ignored
      programmaticScrollTarget.current = null;

      // Throttle the direction check + state update using RAF
      if (throttled) return;
      throttled = true;

      // Capture position at event time — don't re-read inside RAF
      // (scrollToBottom could fire between event and RAF, contaminating scrollTop)
      const userScrollPosition = currentPosition;

      listenerRafHandle.current = requestAnimationFrame(() => {
        throttled = false;

        // Detect scroll direction using captured position
        const scrollingUp = userScrollPosition < previousScrollTop.current;

        if (scrollingUp) {
          setIsAutoScrollEnabled(false);
        } else {
          // Scrolling down - check if near bottom AND streaming is active
          const nearBottom = isNearBottom(container, reEnableThreshold);
          const shouldEnable = nearBottom && isStreaming;
          isAutoScrollEnabledRef.current = shouldEnable;
          setIsAutoScrollEnabled(shouldEnable);
        }

        // Update previous scroll position for next comparison
        previousScrollTop.current = userScrollPosition;
      });
    };

    // Add passive listener for better performance
    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (listenerRafHandle.current) {
        cancelAnimationFrame(listenerRafHandle.current);
      }
    };
  }, [containerRef, isNearBottom, reEnableThreshold, isStreaming]);

  /**
   * Cleanup RAF handles on unmount
   */
  useEffect(() => {
    return () => {
      if (scrollRafHandle.current) {
        cancelAnimationFrame(scrollRafHandle.current);
      }
      if (listenerRafHandle.current) {
        cancelAnimationFrame(listenerRafHandle.current);
      }
    };
  }, []);

  return {
    onContentUpdate,
    scrollToBottom,
    isAutoScrollEnabled, // For debugging
  };
}

export default useStreamingAutoScroll;
